import { Suspense } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';

import { IconLoader2 } from '@tabler/icons-react';

import {
  InteractionType,
  type PublicClientApplication,
} from '@azure/msal-browser';

import AppLayout from './layout/AppLayout';
import { CustomNavigationClient } from './lib/NavigationClient';
import { loginRequest } from './config/msalConfig';
import Applications from './modules/application';
import Settings from './modules/settings';
import Evaluation from './modules/evaluation';
import ProtectedRoute from './components/ProtectedRoute';
import { reactPlugin } from './config/telemetry';

import { UserRole } from './types';

interface AppProps {
  msalInstance: PublicClientApplication;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const SuspenseFallback = () => (
  <div className="flex h-full w-full items-center justify-center">
    <IconLoader2 className="animate-spin text-[#005b92]" size={32} />
  </div>
);

const OnboardRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/applications/onboard${search}`} replace />;
};

function App({ msalInstance }: AppProps) {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route
              element={
                <MsalAuthenticationTemplate
                  interactionType={InteractionType.Redirect}
                  authenticationRequest={loginRequest}
                >
                  <AppLayout />
                </MsalAuthenticationTemplate>
              }
            >
              <Route path="" element={<Navigate to="applications" replace />} />
              <Route
                path="applications/*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Applications />
                  </Suspense>
                }
              />
              <Route path="onboard" element={<OnboardRedirect />} />
              <Route
                path="settings/*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <ProtectedRoute requiredRoles={[UserRole.CHECKPOINT_MANAGER]}>
                      <Settings />
                    </ProtectedRoute>
                  </Suspense>
                }
              />
              <Route
                path="evaluations/*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Evaluation />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </QueryClientProvider>
      </MsalProvider>
    </AppInsightsContext.Provider>
  );
}

export default App;
